import React, { useState } from 'react';
import { 
  Stethoscope, 
  User, 
  Mail, 
  Phone, 
  Briefcase, 
  Plus, 
  Trash2, 
  Lock, 
  CheckCircle2, 
  ShieldCheck, 
  Sparkles, 
  Calendar, 
  Users, 
  FileText, 
  ArrowRight,
  AlertCircle,
  Eye,
  EyeOff,
  Building2,
  DollarSign
} from 'lucide-react';

const COMMON_SPECIALTIES = [
  "Medicina General",
  "Pediatría",
  "Ginecología y Obstetricia",
  "Cardiología",
  "Dermatología",
  "Ortopedia y Traumatología",
  "Oftalmología",
  "Otorrinolaringología",
  "Psiquiatría",
  "Nutrición Clínica",
  "Odontología / Cirugía Oral",
  "Neurología",
  "Medicina Interna",
  "Endocrinología"
];

const DEFAULT_SERVICES = [
  { id: 1, name: 'Consulta Médica General', price: '700' },
  { id: 2, name: 'Consulta de Seguimiento / Control', price: '500' }
];

export const AltaConsultorio =() => {
  const [prefix, setPrefix] = useState('Dr.');
  const [fullName, setFullName] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Dynamic services array
  const [services, setServices] = useState(DEFAULT_SERVICES);

  // Terms and conditions state
  const [acceptTerms, setAcceptTerms] = useState(false);

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleAddService = () => {
    setServices([
      ...services,
      { id: Date.now(), name: '', price: '' }
    ]);
  };

  const handleRemoveService = (id) => {
    if (services.length <= 2) {
      setErrors((prev) => ({
        ...prev,
        services: 'Se requiere registrar un mínimo de 2 servicios médicos.'
      }));
      return;
    }
    setServices(services.filter((s) => s.id !== id));
    // Clear error if now valid length
    if (services.length - 1 >= 2) {
      setErrors((prev) => {
        const newErr = { ...prev };
        delete newErr.services;
        return newErr;
      });
    }
  };

  const handleServiceChange = (id, field, value) => {
    setServices(
      services.map((service) => {
        if (service.id === id) {
          return { ...service, [field]: value };
        }
        return service;
      })
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!fullName.trim()) {
      newErrors.fullName = 'Por favor ingresa tu nombre completo.';
    }

    if (!specialty.trim()) {
      newErrors.specialty = 'Selecciona o escribe tu especialidad.';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Ingresa un número celular válido.';
    } else if (phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'El número debe contener al menos 10 dígitos.';
    }

    if (!email.trim()) {
      newErrors.email = 'El correo electrónico es requerido.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Ingresa un correo electrónico válido.';
    }

    if (!password) {
      newErrors.password = 'Crea una contraseña para tu cuenta.';
    } else if (password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres.';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden.';
    }

    // Services validation: at least 2 non-empty valid items
    const validServices = services.filter(
      (s) => s.name.trim() !== '' && s.price !== '' && !isNaN(Number(s.price)) && Number(s.price) > 0
    );

    if (services.length < 2) {
      newErrors.services = 'Debes mantener al menos 2 servicios en tu perfil.';
    } else if (validServices.length < 2) {
      newErrors.services = 'Debes completar el nombre y precio válido para al menos 2 servicios.';
    }

    if (!acceptTerms) {
      newErrors.terms = 'Debes aceptar los Términos de Servicio y el Aviso de Privacidad.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API registration call
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSuccess(true);
      }, 1200);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 md:p-8 font-sans antialiased text-slate-800">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-12 text-center animate-fade-in relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-60 h-60 bg-emerald-100/60 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-24 -left-24 w-60 h-60 bg-teal-100/60 rounded-full blur-3xl pointer-events-none"></div>

          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <CheckCircle2 className="w-12 h-12" />
          </div>

          <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">
            ¡Registro Exitoso!
          </span>

          <h2 className="text-3xl font-extrabold text-slate-900 mb-2">
            ¡Bienvenido(a), {prefix} {fullName}!
          </h2>
          <p className="text-slate-600 mb-8 max-w-lg mx-auto text-base">
            Tu cuenta en <strong className="text-teal-700">Consultorio Digital</strong> ha sido creada correctamente. Ya puedes comenzar a organizar tus expedientes, citas y cobros.
          </p>

          {/* Account Summary Card */}
          <div className="bg-slate-50 rounded-2xl p-6 text-left mb-8 border border-slate-200/80 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-teal-600" /> Resumen de tu Consultorio
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-6">
              <div>
                <span className="text-slate-500 block text-xs">Especialidad:</span>
                <span className="font-semibold text-slate-800">{specialty}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-xs">Correo Electrónico:</span>
                <span className="font-semibold text-slate-800">{email}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-xs">Teléfono de Contacto:</span>
                <span className="font-semibold text-slate-800">{countryCode} {phone}</span>
              </div>
              <div>
                <span className="text-slate-500 block text-xs">Estado de Cuenta:</span>
                <span className="inline-flex items-center gap-1 text-emerald-700 font-medium text-xs bg-emerald-100/80 px-2 py-0.5 rounded-full">
                  Activa - Prueba Gratis (14 Días)
                </span>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                Servicios Iniciales Configurados:
              </span>
              <ul className="space-y-2">
                {services
                  .filter((s) => s.name.trim() !== '')
                  .map((s, index) => (
                    <li key={s.id || index} className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-slate-100 text-xs">
                      <span className="font-medium text-slate-700 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                        {s.name}
                      </span>
                      <span className="font-bold text-teal-700">${Number(s.price).toLocaleString('es-MX')} MXN</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <button
            onClick={() => {
              setIsSuccess(false);
              setFullName('');
              setEmail('');
              setPassword('');
              setConfirmPassword('');
              setPhone('');
            }}
            className="w-full sm:w-auto px-8 py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
          >
            Ir a mi Panel Médico <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 font-sans antialiased text-slate-800 flex flex-col justify-between">
      {/* Header Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-gradient-to-tr from-teal-600 to-emerald-500 text-white p-2 rounded-xl shadow-md">
              <Stethoscope className="w-6 h-6" />
            </div>
            <div>
              <span className="text-xl font-black tracking-tight text-slate-900">
                Consultorio<span className="text-teal-600">Digital</span>
              </span>
              <span className="hidden sm:inline-block ml-2 text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium border border-teal-200">
                Plataforma Médica
              </span>
            </div>
          </div>

          <div className="text-xs sm:text-sm text-slate-600">
            ¿Ya tienes una cuenta?{' '}
            <a href="/" className="text-teal-600 hover:text-teal-700 font-bold transition-colors underline">
              Iniciar Sesión
            </a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 md:py-12 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-teal-500/20 rounded-full blur-2xl pointer-events-none"></div>

              <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 text-xs font-semibold px-3 py-1 rounded-full border border-teal-500/30 mb-6">
                <Sparkles className="w-3.5 h-3.5" /> Todo en una sola plataforma
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4 leading-snug">
                Optimiza la gestión de tu práctica médica
              </h1>
              <p className="text-slate-300 text-sm leading-relaxed mb-8">
                Únete a más de 3,500 profesionales de la salud que ahorran hasta 10 horas semanales en tareas administrativas.
              </p>

              {/* Platform perks list */}
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-teal-500/20 rounded-lg text-teal-400 mt-0.5">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Agenda Médica Inteligente</h4>
                    <p className="text-xs text-slate-400">Visualiza tus citas, diario o semanal.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-teal-500/20 rounded-lg text-teal-400 mt-0.5">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Expediente Clínico Electrónico</h4>
                    <p className="text-xs text-slate-400">Notas de evolución, laboratorios, carga documentos y mucho más.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-teal-500/20 rounded-lg text-teal-400 mt-0.5">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Catálogo de Servicios y Pagos</h4>
                    <p className="text-xs text-slate-400">Control claro de tus ingresos por consulta y tratamiento.</p>
                  </div>
                </div>
              </div>

              {/* Doctor Testimonial Card */}
              <div className="mt-8 pt-6 border-t border-slate-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center text-sm shadow-md">
                  DR
                </div>
                <div>
                  <p className="text-xs italic text-slate-300">
                    "Consultorio Digital transformó la atención a mis pacientes. Todo organizado y ágil."
                  </p>
                  <p className="text-[11px] font-bold text-teal-400 mt-0.5">Dr. Roberto Garza - Internista</p>
                </div>
              </div>
            </div>

            {/* Support info badge */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-800">Seguridad & Privacidad Garantizada</p>
                <p className="text-[11px] text-slate-500">Encriptación de grado médico para la protección de tus datos.</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-slate-200/70">
            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Registro de Nuevo Médico
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Completa tus datos profesionales e ingresa al menos dos servicios para activar tu perfil.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-8">
              
              {/* SECTION 1: Personal / Professional Info */}
              {}
              <div className="space-y-5">
                <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                  <User className="w-5 h-5 text-teal-600" />
                  <h3 className="font-bold text-slate-800 text-base">1. Información del Especialista</h3>
                </div>

                {/* Name & Prefix */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Nombre Completo del Doctor / Doctora <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      className="w-24 px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-slate-800 text-sm font-medium focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none transition-all"
                    >
                      <option value="Dr.">Dr.</option>
                      <option value="Dra.">Dra.</option>
                      <option value="Lic.">Lic.</option>
                    </select>

                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="ej. Carlos Mendoza Ramírez"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className={`w-full px-4 py-2.5 bg-slate-50 border ${
                          errors.fullName ? 'border-rose-500 focus:ring-rose-200' : 'border-slate-300 focus:ring-teal-500'
                        } rounded-xl text-slate-800 text-sm focus:ring-2 focus:border-teal-500 focus:outline-none transition-all`}
                      />
                    </div>
                  </div>
                  {errors.fullName && (
                    <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Specialty Selection */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Especialidad Médica <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      list="specialties-list"
                      placeholder="Selecciona o escribe tu especialidad (ej. Cardiología)"
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2.5 bg-slate-50 border ${
                        errors.specialty ? 'border-rose-500 focus:ring-rose-200' : 'border-slate-300 focus:ring-teal-500'
                      } rounded-xl text-slate-800 text-sm focus:ring-2 focus:border-teal-500 focus:outline-none transition-all`}
                    />
                    <Briefcase className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <datalist id="specialties-list">
                      {COMMON_SPECIALTIES.map((spec) => (
                        <option key={spec} value={spec} />
                      ))}
                    </datalist>
                  </div>
                  {errors.specialty && (
                    <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.specialty}
                    </p>
                  )}
                </div>

                {/* Contact Row: Phone & Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Número de Celular <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-slate-300 bg-slate-100 text-slate-600 text-xs font-medium">
                        {countryCode}
                      </span>
                      <div className="relative flex-1">
                        <input
                          type="tel"
                          placeholder="55 1234 5678"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className={`w-full pl-9 pr-3 py-2.5 bg-slate-50 border ${
                            errors.phone ? 'border-rose-500' : 'border-slate-300'
                          } rounded-r-xl text-slate-800 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none transition-all`}
                        />
                        <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                      </div>
                    </div>
                    {errors.phone && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Correo Electrónico <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="doctor@ejemplo.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2.5 bg-slate-50 border ${
                          errors.email ? 'border-rose-500 focus:ring-rose-200' : 'border-slate-300 focus:ring-teal-500'
                        } rounded-xl text-slate-800 text-sm focus:ring-2 focus:border-teal-500 focus:outline-none transition-all`}
                      />
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {errors.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {}
              <div className="space-y-5 pt-2">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                    <div>
                      <h3 className="font-bold text-slate-800 text-base">
                        2. Servicios Médicos y Precios
                      </h3>
                    </div>
                  </div>
                  <span className="text-xs bg-amber-50 text-amber-700 font-semibold px-2.5 py-1 rounded-md border border-amber-200">
                    Mínimo 2 servicios
                  </span>
                </div>

                <p className="text-xs text-slate-500">
                  Agrega los conceptos de consulta o procedimientos que ofreces a tus pacientes junto con su costo estimado en pesos mexicanos (MXN).
                </p>

                {/* Services Dynamic Cards */}
                <div className="space-y-3">
                  {services.map((service, index) => (
                    <div
                      key={service.id}
                      className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-200 transition-all hover:border-slate-300"
                    >
                      <div className="flex-1">
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                          Servicio #{index + 1}
                        </label>
                        <input
                          type="text"
                          placeholder="ej. Consulta General, Ecografía, Electrocardiograma..."
                          value={service.name}
                          onChange={(e) => handleServiceChange(service.id, 'name', e.target.value)}
                          className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                        />
                      </div>

                      <div className="w-full sm:w-44">
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                          Precio (MXN)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-slate-400 text-sm font-semibold">$</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="700"
                            value={service.price}
                            onChange={(e) => handleServiceChange(service.id, 'price', e.target.value)}
                            className="w-full pl-7 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-sm font-semibold focus:ring-2 focus:ring-teal-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-end justify-end sm:self-end pt-1 sm:pt-0">
                        <button
                          type="button"
                          onClick={() => handleRemoveService(service.id)}
                          disabled={services.length <= 2}
                          title={services.length <= 2 ? "Se requiere un mínimo de 2 servicios" : "Eliminar servicio"}
                          className={`p-2 rounded-lg border transition-colors ${
                            services.length <= 2
                              ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                              : 'bg-white text-slate-500 hover:text-rose-600 hover:bg-rose-50 border-slate-300'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {errors.services && (
                  <p className="text-xs text-rose-500 font-medium flex items-center gap-1 bg-rose-50 p-2.5 rounded-lg border border-rose-200">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {errors.services}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleAddService}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-bold rounded-xl border border-teal-200 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Agregar otro servicio
                </button>
              </div>

              {}
              <div className="space-y-5 pt-2">
                <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                  <Lock className="w-5 h-5 text-teal-600" />
                  <h3 className="font-bold text-slate-800 text-base">3. Seguridad de la Cuenta</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Password */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Contraseña <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mínimo 8 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full pl-10 pr-10 py-2.5 bg-slate-50 border ${
                          errors.password ? 'border-rose-500' : 'border-slate-300'
                        } rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none transition-all`}
                      />
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Confirmar Contraseña <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Repite tu contraseña"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2.5 bg-slate-50 border ${
                          errors.confirmPassword ? 'border-rose-500' : 'border-slate-300'
                        } rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none transition-all`}
                      />
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                {/* Terms Checkbox */}
                <div className="pt-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      Acepto los{' '}
                      <a href="#terms" className="text-teal-600 underline font-semibold hover:text-teal-700">
                        Términos y Condiciones del Servicio
                      </a>{' '}
                      y el{' '}
                      <a href="#privacy" className="text-teal-600 underline font-semibold hover:text-teal-700">
                        Aviso de Privacidad
                      </a>{' '}
                      de Consultorio Digital. Reconozco que mis datos estarán protegidos bajo normativa vigente.
                    </span>
                  </label>
                  {errors.terms && (
                    <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> {errors.terms}
                    </p>
                  )}
                </div>
              </div>

              {}
              <div className="pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-bold text-base rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creando tu Consultorio Digital...</span>
                    </div>
                  ) : (
                    <>
                      <span>Completar Registro y Comenzar</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                <p className="text-center text-[11px] text-slate-400 mt-3">
                  Prueba gratuita de 14 días sin requerir tarjeta de crédito.
                </p>
              </div>

            </form>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 mt-12 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p>© {new Date().getFullYear()} Consultorio Digital Inc. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            <a href="#privacy" className="hover:text-slate-800">Privacidad</a>
            <a href="#terms" className="hover:text-slate-800">Términos</a>
            <a href="#support" className="hover:text-slate-800">Soporte Médico</a>
          </div>
        </div>
      </footer>
    </div>
  );
}